import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import paymentTypeService from '../../../../services/paymentTypeService';
import './PaymentTypeListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const PaymentTypeListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await paymentTypeService.getPaymentTypes(1, 100);
      setItems(response?.data ?? []);
    } catch {
      setError(t('paymentType.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await paymentTypeService.getPaymentTypes(1, 100);
      setItems(response?.data ?? []);
    } catch {
      setError(t('paymentType.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'code',
      label: t('paymentType.code'),
      sortable: true,
      width: '120px',
      render: (value) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '13px' }}>{value}</span>
      ),
    },
    {
      key: 'name',
      label: t('paymentType.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'description',
      label: t('common.description'),
      sortable: false,
      width: '260px',
      hideTablet: true,
      hideMobile: true,
      render: (value) => (
        <span className="pt-description-text">{value || '—'}</span>
      ),
    },
    {
      key: 'paymentCategory',
      label: t('paymentType.paymentCategory'),
      sortable: true,
      width: '110px',
      render: (value: string) => {
        const map: Record<string, { icon: string; cls: string }> = {
          Cash:   { icon: '💵', cls: 'pt-cat-cash'   },
          Credit: { icon: '🏦', cls: 'pt-cat-credit' },
          Other:  { icon: '💳', cls: 'pt-cat-other'  },
        };
        const cfg = map[value] ?? map['Other'];
        return (
          <span className={`pt-cat-badge ${cfg.cls}`}>
            {cfg.icon} {value}
          </span>
        );
      },
    },
    {
      key: 'usageScope',
      label: t('paymentType.usageScope', 'Usage Scope'),
      sortable: true,
      width: '110px',
      render: (value: string) => {
        const map: Record<string, { icon: string }> = {
          Sales:    { icon: '🛒' },
          Purchase: { icon: '📦' },
          Both:     { icon: '🔄' },
        };
        const cfg = map[value ?? 'Both'] ?? map['Both'];
        return (
          <span className="pt-cat-badge pt-cat-badge-other">
            {cfg.icon} {value ?? 'Both'}
          </span>
        );
      },
    },
    {
      key: 'isDefault',
      label: t('paymentType.isDefault'),
      width: '100px',
      hideMobile: true,
      render: (value) =>
        value ? (
          <span className="pt-badge pt-badge-default">✓ {t('paymentType.default')}</span>
        ) : null,
    },
    {
      key: 'sortOrder',
      label: t('paymentType.sortOrder'),
      sortable: true,
      width: '90px',
      hideMobile: true,
      render: (value) => <span style={{ color: '#9ca3af', fontSize: '13px' }}>{value}</span>,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '100px',
      render: (value) => (
        <span className={value ? 'pt-badge pt-badge-active' : 'pt-badge pt-badge-inactive'}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      key: 'createdDate',
      label: t('common.createdDate'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value) =>
        new Date(value).toLocaleDateString(
          t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US'
        ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/settings/payment-types/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/settings/payment-types/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/settings/payment-types/${item.id}/delete`),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
            <h1 className="title">{t('paymentType.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/settings/payment-types/create')}
              disabled={loading}
            >
            <CirclePlus size={18} strokeWidth={1.75} />
              {t('paymentType.newPaymentType')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={1.75} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={15}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('paymentType.noPaymentTypesFound')}
          rowsPerPageOptions={[10, 15, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default PaymentTypeListPage;
